/**
 * LearnPage — Help & tutorials, as a full page of the platform.
 *
 * Three columns, in reading order (so the grid mirrors itself under RTL):
 *
 *   the library   every tutorial this person can reach, in sections,
 *                 searchable, with the one that matches where they are
 *                 in their tour marked "for you";
 *   the player    the selected tutorial — film with its captions beneath,
 *                 the beats, the written steps, a way to the place itself;
 *   the Guide     a real conversation with the agent that knows the
 *                 interface. When it opens a tutorial, the library selects
 *                 it and the player plays it.
 *
 * The selection lives in the URL (`?topic=`) so a tutorial can be linked
 * to from anywhere — a step card, a colleague, the Guide's marker.
 */

import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTenantPath } from '@/routing';
import { useLanguage } from '@/hooks/useLanguage';
import { Sidebar } from '@/components/layout/Sidebar';
import { searchTopics, visibleSections, visibleTopics, type LearnTopic } from '../content/catalog';
import { useLearnViewer } from '../hooks/useLearnViewer';
import { useJourney } from '../hooks/useJourney';
import { GuideChat } from '../components/GuideChat';
import { TutorialPlayer } from '../components/TutorialPlayer';
import './learnPage.css';

export function LearnPage() {
  const { t, language } = useLanguage();
  const lang: 'en' | 'he' = language === 'he' ? 'he' : 'en';
  const navigate = useNavigate();
  const tenantPath = useTenantPath();
  const [params, setParams] = useSearchParams();
  const viewer = useLearnViewer();
  const journey = useJourney();

  const mine = useMemo(() => visibleTopics(viewer), [viewer]);
  const [query, setQuery] = useState('');
  const found = useMemo(() => searchTopics(mine, query, lang), [mine, query, lang]);
  const sections = useMemo(() => visibleSections(found), [found]);

  // "Based on the level you are at": the tutorial for the step they are on
  // in their tour, else the first with a film. Marked in the list too.
  const forYouId = useMemo(() => {
    const clip = journey?.currentStep?.clip;
    const byClip = clip ? mine.find((x) => x.film === clip) : undefined;
    return byClip?.id ?? mine.find((x) => Boolean(x.film))?.id ?? mine[0]?.id ?? null;
  }, [journey?.currentStep?.clip, mine]);

  const selectedId = params.get('topic') ?? forYouId;
  const selected = useMemo(() => mine.find((x) => x.id === selectedId) ?? null, [mine, selectedId]);

  const [playKey, setPlayKey] = useState(0);
  const [why, setWhy] = useState<string | null>(null);

  // The Guide column folds to a rail; the choice sticks across visits.
  const [guideOpen, setGuideOpen] = useState(() => localStorage.getItem('learn.guideOpen') !== 'false');
  const setGuide = useCallback((open: boolean) => {
    setGuideOpen(open);
    localStorage.setItem('learn.guideOpen', open ? 'true' : 'false');
  }, []);

  // Start the tour over: progress to zero, then home, where the welcome map
  // comes back on its own.
  const restartTour = useCallback(() => {
    journey?.restart();
    navigate(tenantPath('/'));
  }, [journey, navigate, tenantPath]);

  const select = useCallback(
    (id: string, opts: { play?: boolean; why?: string | null } = {}) => {
      setParams({ topic: id }, { replace: true });
      setWhy(opts.why ?? null);
      if (opts.play) setPlayKey((k) => k + 1);
    },
    [setParams],
  );

  const goTo = useCallback(
    (topic: LearnTopic) => {
      if (topic.route === undefined) return;
      navigate(tenantPath(topic.route || '/'));
    },
    [navigate, tenantPath],
  );

  const tourDone = journey ? journey.completed.size : 0;
  const tourTotal = journey ? journey.chain.length : 0;
  const showTour = Boolean(journey?.active) && tourTotal > 0 && !journey?.skipped;

  return (
    <div className="chat-layout">
      <Sidebar
        options={{
          showHome: true,
          showChat: false,
          showBots: true,
          showSearch: true,
          showAdmin: true,
          showGroupAdmin: true,
          showSuperAdmin: true,
          showConversations: true,
          showBotsList: true,
          activeItem: 'learn',
        }}
      />
      <main className="chat-main-content lp-shell">
      <div className={`lp ${guideOpen ? '' : 'guide-collapsed'}`} data-testid="learn-page">
      {/* ── the library ── */}
      <aside className="lp-library" aria-label={t('learn.title')}>
        <header className="lp-library-head">
          <h2>{t('learn.title')}</h2>
          <p>{t('learn.librarySub')}</p>
        </header>

        <input
          className="lp-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('learn.searchPlaceholder')}
          aria-label={t('learn.searchPlaceholder')}
          data-testid="learn-search"
        />

        {showTour && (
          <div className="lp-level" data-testid="learn-level">
            <span className="lp-level-rank">{t(`journey.ranks.${journey!.rank}.name`)}</span>
            <span className="lp-level-count">
              {t('journey.dock.count', { done: String(tourDone), total: String(tourTotal) })}
            </span>
            <span className="lp-level-bar" aria-hidden>
              <i style={{ width: `${tourTotal ? (tourDone / tourTotal) * 100 : 0}%` }} />
            </span>
          </div>
        )}

        {journey?.active && (
          <div className="lp-tour-actions">
            <button className="lp-restart" onClick={restartTour} data-testid="learn-restart-tour">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 3v6h6" />
              </svg>
              {t(journey.finished || journey.skipped ? 'learn.takeTourAgain' : 'learn.restartTour')}
            </button>
          </div>
        )}

        <div className="lp-library-scroll">
          {found.length === 0 && <p className="lp-empty">{t('learn.noResults')}</p>}
          {sections.map((section) => {
            const topics = found.filter((x) => x.section === section.id);
            if (topics.length === 0) return null;
            return (
              <section className="lp-section-group" key={section.id}>
                <h3 dir="auto">{section.title[lang]}</h3>
                {topics.map((topic) => {
                  const isSel = topic.id === selectedId;
                  const isForYou = showTour && topic.id === forYouId && !journey?.finished;
                  return (
                    <button
                      key={topic.id}
                      className={`lp-row ${isSel ? 'is-selected' : ''} ${topic.film ? 'has-film' : ''}`}
                      onClick={() => select(topic.id, { play: Boolean(topic.film) })}
                      aria-current={isSel ? 'true' : undefined}
                      data-testid={`learn-topic-${topic.id}`}
                    >
                      <span className="lp-row-glyph" aria-hidden>
                        {topic.film ? (
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                        ) : (
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 4h9l5 5v11H5z" /><path d="M14 4v5h5" /></svg>
                        )}
                      </span>
                      <span className="lp-row-main">
                        <span className="lp-row-title" dir="auto">
                          {topic.title[lang]}
                          {isForYou && <em className="lp-row-foryou">{t('learn.forYou')}</em>}
                        </span>
                        <span className="lp-row-summary" dir="auto">{topic.summary[lang]}</span>
                      </span>
                    </button>
                  );
                })}
              </section>
            );
          })}
        </div>
      </aside>

      {/* ── the player ── */}
      <main className="lp-main">
        <TutorialPlayer topic={selected} lang={lang} playKey={playKey} why={why} onGoTo={goTo} />
      </main>

      {/* ── the Guide ── */}
      {/* Hidden, not unmounted, when folded: the conversation must survive
          the fold, and its streaming state lives inside the component. */}
      <aside className="lp-side" aria-label={t('learn.guideTitle')} hidden={!guideOpen}>
        <GuideChat
          topics={mine}
          lang={lang}
          onShowTutorial={(id, reason) => select(id, { play: true, why: reason })}
          onRevisitTutorial={(id) => select(id, { play: true })}
          onCollapse={() => setGuide(false)}
        />
      </aside>
      {!guideOpen && (
        <button
          className="lp-side-rail"
          onClick={() => setGuide(true)}
          aria-label={t('learn.showGuide')}
          title={t('learn.showGuide')}
          data-testid="guide-expand"
        >
          <span className="lp-guide-mark" aria-hidden>?</span>
          <span className="lp-side-rail-label">{t('learn.guideTitle')}</span>
        </button>
      )}
      </div>
      </main>
    </div>
  );
}

export default LearnPage;
